// Copyright 2026 Bitech.id. Licensed under the MIT License.

import type { BiwaveClient } from '@biwave/core';

function resolveUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.href;
  return input.url;
}

function resolveMethod(input: RequestInfo | URL, init: RequestInit | undefined): string {
  if (init?.method) return init.method;
  if (input instanceof Request) return input.method;
  return 'GET';
}

export function installFetchBreadcrumbs(client: BiwaveClient): () => void {
  if (typeof window.fetch !== 'function') return () => {};

  const original = window.fetch.bind(window);
  // Never breadcrumb the SDK's own requests to its ingest endpoint — that's
  // noise, not app behavior, and Sentry's own SDKs exclude their DSN host too.
  const ingestOrigin = new URL(client.target.endpoint).origin;

  window.fetch = ((input: RequestInfo | URL, init?: RequestInit) => {
    const url = resolveUrl(input);
    if (url.startsWith(ingestOrigin)) {
      return original(input, init);
    }

    const method = resolveMethod(input, init);
    const startTime = Date.now();

    return original(input, init).then(
      (response) => {
        client.addBreadcrumb({
          type: 'http',
          category: 'fetch',
          level: response.status >= 400 ? 'error' : 'info',
          data: {
            url,
            method,
            status_code: response.status,
            duration_ms: Date.now() - startTime,
          },
        });
        return response;
      },
      (error: unknown) => {
        client.addBreadcrumb({
          type: 'http',
          category: 'fetch',
          level: 'error',
          data: { url, method, status_code: 0, duration_ms: Date.now() - startTime },
        });
        throw error;
      },
    );
  }) as typeof fetch;

  return () => {
    window.fetch = original;
  };
}
